import { FocusEvent, ForwardedRef, forwardRef, InputHTMLAttributes, useEffect, useRef, useState } from 'react';
import { cn } from 'utils/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  label?: string;
  error?: boolean;
  type?: string;
  containerClassName?: string;
  labelClassName?: string;
  isTextArea?: boolean;
  dataTestID?: string;
}

const mergeRefs = (...refs: ForwardedRef<HTMLInputElement | HTMLTextAreaElement>[]) => {
  const filteredRefs = refs.filter(Boolean);

  if (!filteredRefs.length) return null;
  if (filteredRefs.length === 0) return filteredRefs[0];
  return (inst: HTMLInputElement | HTMLTextAreaElement | null) => {
    for (const ref of filteredRefs) {
      if (typeof ref === 'function') {
        ref(inst);
      } else if (ref) {
        ref.current = inst;
      }
    }
  };
};

const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      label,
      error = false,
      type = 'text',
      className,
      containerClassName,
      labelClassName,
      autoFocus,
      isTextArea,
      dataTestID,
      disabled,
      value,
      ...props
    },
    ref
  ) => {
    const withPlaceholderAndLabel = props.placeholder && label;
    const inputRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      if (inputRef.current && autoFocus) {
        inputRef.current.focus?.();
      }
      if (withPlaceholderAndLabel && !autoFocus) {
        inputRef.current.placeholder = '';
      }
    }, []);

    const handleFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (withPlaceholderAndLabel) {
        event.target.placeholder = props.placeholder;
      }
      props?.onFocus?.(event);
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (withPlaceholderAndLabel) {
        event.target.placeholder = '';
      }
      props?.onBlur?.(event);
    };

    const shouldMoveLabelToTop = isFocused || (disabled && !!value) || inputRef?.current?.value || value;

    // generate unique ID
    const randomString = Math.random().toString(36);

    /**
     * Input Component
     */
    const inputComponent = (
      <>
        <input
          // @ts-expect-error - Ref type?
          ref={mergeRefs(ref, inputRef)}
          id={props.name + randomString}
          type={type}
          className={cn(
            'block h-full w-full appearance-none rounded-lg border-0 bg-transparent px-4 py-3.5 font-sans text-sm placeholder:hidden placeholder:text-sm placeholder:text-slate-500 focus:text-slate-700 focus:outline-none focus:ring-0',
            className
          )}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          value={value}
          {...props}
        />
        {label && (
          <label
            htmlFor={props.name + randomString}
            onClick={() => inputRef?.current?.focus?.()}
            className={cn(
              'z-1 absolute left-3 top-1/2 box-border h-auto origin-0 -translate-y-1/2 cursor-text rounded-lg bg-white px-1 text-sm duration-150',
              shouldMoveLabelToTop && '!-top-[1.5px] text-xs',
              labelClassName
            )}
          >
            {label}
          </label>
        )}
      </>
    );

    /**
     * TextArea Component
     */
    const textAreaComponent = (
      <>
        <textarea
          // @ts-expect-error - Ref type?
          ref={mergeRefs(ref, inputRef)}
          id={props.name + randomString}
          className={cn(
            'block !w-full appearance-none rounded-lg border-0 bg-transparent px-4 py-3.5 font-sans !text-sm placeholder:text-slate-500 focus:text-slate-700 focus:outline-none focus:ring-0',
            className
          )}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          value={value}
          {...props}
        />
        {label && (
          <label
            htmlFor={props.name + randomString}
            onClick={() => inputRef?.current?.focus?.()}
            className={cn(
              'z-1 absolute left-3 top-[14px] box-border h-auto origin-0 !scale-x-100 !scale-y-100 cursor-text rounded-lg bg-white px-1 text-sm duration-150',
              shouldMoveLabelToTop && '!-top-[10px] text-xs',
              labelClassName
            )}
          >
            {label}
          </label>
        )}
      </>
    );

    return (
      <div
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={cn(
          'relative z-0 my-2 rounded-lg border border-slate-300 bg-white p-[1px] text-slate-400 outline outline-0 transition-all focus-within:border-indigo-500 focus-within:text-indigo-500',
          !isTextArea && 'h-12',
          containerClassName
        )}
        data-testid={dataTestID}
      >
        {isTextArea ? textAreaComponent : inputComponent}
      </div>
    );
  }
);

export default Input;
